import type {
  StripeRedirectDto,
  SubscriptionCheckoutInput,
  SubscriptionCheckoutResultDto,
  SubscriptionDto,
} from '@osteopro/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, unwrap } from '../../lib/api.js';
import { runAuthed } from '../../lib/auth-store.js';
import { subscriptionKeys } from '../../lib/query-keys.js';

/**
 * L'abonnement du cabinet actif.
 *
 * **Aucune donnée de carte ne passe par ici.** Le portail ne rend qu'une URL. « Choisir cette
 * offre » rend soit cette URL de caisse, soit le constat que l'abonnement déjà ouvert a changé
 * de tarif — la saisie, quand elle a lieu, est sur une page hébergée par le prestataire.
 *
 * L'abonnement n'est pas dans le cache des droits (`/api/plans/entitlements`) : les droits sont
 * lus par tous les écrans et doivent rester légers. Un changement de tarif écrit en base tout
 * de suite ; le rappel du prestataire confirme la même chose.
 */

/**
 * `enabled` existe pour le bandeau d'impayé : il vit dans la coque, donc sur **tous** les écrans,
 * et n'a pas à provoquer une requête de plus à chaque page. Il lit la condition dans les droits,
 * déjà en cache, et n'ouvre celle-ci que dans le cas rare où elle sert.
 */
export function useSubscription(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.current(),
    queryFn: async (): Promise<SubscriptionDto> =>
      runAuthed(async () => unwrap(await api.api.subscription.$get())),
    enabled,
  });
}

/**
 * « Choisir cette offre ». Sans abonnement vivant, la réponse porte l'URL de caisse. Avec un
 * abonnement déjà ouvert, elle dit que le tarif a été mis à jour, ou que c'était déjà celui-là.
 * L'écran invalide alors les droits : le rappel du prestataire confirmera la même écriture.
 */
export function useSubscriptionCheckout() {
  return useMutation({
    mutationFn: async (input: SubscriptionCheckoutInput): Promise<SubscriptionCheckoutResultDto> =>
      runAuthed(async () => unwrap(await api.api.subscription.checkout.$post({ json: input }))),
  });
}

/** Ouvre le portail client : moyen de paiement, factures, résiliation. Tout est chez eux. */
export function useSubscriptionPortal() {
  return useMutation({
    mutationFn: async (): Promise<StripeRedirectDto> =>
      runAuthed(async () => unwrap(await api.api.subscription.portal.$post())),
  });
}

/**
 * Quitte l'application pour une page du prestataire. `assign` et non `replace` : le retour
 * arrière du navigateur doit ramener sur l'écran d'abonnement, pas sur une page de paiement
 * abandonnée.
 */
export function goToStripe(url: string): void {
  globalThis.location.assign(url);
}
